import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Card, Drawer, IconButton, List, ListItem, Menu, MenuItem, Stack, Typography, Switch, useTheme, FormControlLabel, useMediaQuery } from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from "react-i18next";
import { useState, useCallback } from "react";
import ButtonComponent from "../../components/form/buttonComponent";
import MenuIcon from '@mui/icons-material/Menu';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './navigation.css';
import { styled } from '@mui/material/styles';
import { GetTheme, THEME } from "../../theme/customThemeProvider";
import GetQuote from "../contactUs/getQuote";
import companyData from '../../assets/data/company.json';

const NAV_OPTIONS = [
    {
        id: 0,
        label: "services", 
        sub_menu: [
            {label: "ai", to: "/ai"},
            {label: "game", to: "/games"},
            {label: "digital", to: "/digital"}
        ],
        to: "/suite"
    },
    {id: 1, label: "case_studies", sub_menu: [], to: "/case-studies"},
    {id: 2, label: "digital_studio", sub_menu: [], to: "/digital-studio"},
    {id: 3, label: "about_us", sub_menu: [], to: "/about-us"},
    {id: 4, label: "contact_us", sub_menu: [], to: "/contact-us"}
];

const IOSSwitch = styled((props) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 30,
    height: 15,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.background.paper,
                opacity: 1,
                border: 0,
                ...theme.applyStyles('dark', {
                    backgroundColor: theme.palette.primary.main
                }),
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            }
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: theme.palette.background.paper,
            border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: theme.palette.grey[100],
            ...theme.applyStyles('dark', {
                color: theme.palette.grey[600],
            })
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 0.7,
            ...theme.applyStyles('dark', {
                opacity: 0.3,
            })
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 11,
        height: 11
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: '#E9E9EA',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500
        }),
        ...theme.applyStyles('dark', {
            backgroundColor: '#39393D'
        }),
    },
}));

const NavigationLayout = () => {
    const theme = useTheme();
    const { toggleTheme, themeMode } = GetTheme();
    const {t} = useTranslation();

    const navigate = useNavigate();

    const [anchorEl, setAnchorEl] = useState(null);
    const [expandedMenu, setExpandedMenu] = useState(null);
    const openSubMenu = Boolean(anchorEl);

    const [openQuote, setOpenQuote] = useState(false);

    const location = useLocation();

    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const handleSubMenuClick = useCallback((event) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleSubMenuClose = useCallback((redirect) => {
        if(redirect){
            navigate(expandedMenu.to + redirect);
        }

        setAnchorEl(null);

        setTimeout(() => {
            setExpandedMenu(null);
        }, 200);
    }, [expandedMenu]);

    const [openMobileMenu, setOpenMobileMenu] = useState(false);

    const toggleDrawer = (newOpen) => () => {
        setOpenMobileMenu(newOpen);
    };

    const selectNavigationOption = useCallback((event, id) => {
        if(NAV_OPTIONS[id].sub_menu.length > 0){
            setExpandedMenu(NAV_OPTIONS[id]);
            handleSubMenuClick(event);

            return;
        }

        navigate(NAV_OPTIONS[id].to);
    }, [NAV_OPTIONS]);

    const getAQuote = useCallback(() => {
        // setOpenMobileMenu(false);
        // setOpenQuote(true);

        window.open(companyData.contact.formLinks.quotation, "_blank");
    }, []);

    return (
        <>
            <Card
                elevation={0}
                sx={{
                    width: '100%',
                    backgroundColor: theme.palette.background.default,
                    borderRadius: 0,
                    boxShadow: '0px 5px 50px 1px rgba(0, 0, 0, 0.1)',
                    zIndex: 999,
                    position: 'fixed'
                }}
            >
                <Stack
                    direction='row'
                    justifyContent='space-between'
                    alignItems='center'
                    height={{xs: 75, md: 100}}
                    px='5%'
                >
                    {/* Logo */}
                    <Link to="/">
                        <img 
                            src={themeMode === THEME.LIGHT ? "/images/title_logo-light.png" : "/images/title_logo-dark.png"}
                            alt="Logo" 
                            style={{
                                width: isMobile ? '150px' : '200px'
                            }} 
                        />
                    </Link>

                    {/* Navigation Options */}
                    <Stack
                        direction='row'
                        justifyContent='center'
                        alignItems='center'
                        spacing={2}
                        display={{xs: 'none', lg: 'block'}}
                        px={2}
                    >
                        {
                            NAV_OPTIONS.map((item, index) => {
                                return <Button 
                                    key={index}
                                    variant="text"
                                    sx={{
                                        textTransform: 'none',
                                        color: theme.palette.text.primary,
                                        backgroundColor: 'transparent'
                                    }}
                                    disableRipple
                                    onClick={(event) => selectNavigationOption(event, index)}
                                    className="onPointer"
                                >
                                    <Stack
                                        direction='row'
                                        justifyContent='center'
                                        alignItems='center'
                                        spacing={0.5}
                                    >
                                        <Typography
                                            sx={{
                                                fontWeight: location?.pathname.includes(item.to) ? 700 : 500,
                                                fontSize: {xs: 12, md: 14, lg: 16},
                                                textAlign: 'left',
                                                color: location?.pathname.includes(item.to) 
                                                ? themeMode === THEME.LIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText
                                                : theme.palette.text.primary,
                                                '&:hover': {
                                                    color: themeMode === THEME.LIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText
                                                }
                                            }}
                                        >
                                            {t(`navigation.${item.label}`)}
                                        </Typography>

                                        {
                                            item.sub_menu.length > 0
                                            ?
                                            <>
                                                {
                                                    expandedMenu && expandedMenu.id === item.id
                                                    ?
                                                    <KeyboardArrowUpIcon 
                                                        style={{
                                                            width: 20,
                                                            color: location?.pathname.includes(item.to) 
                                                            ? themeMode === THEME.LIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText
                                                            : theme.palette.text.primary
                                                        }}
                                                    />
                                                    :
                                                    <KeyboardArrowDownIcon 
                                                        style={{
                                                            width: 20,
                                                            color: location?.pathname.includes(item.to) 
                                                            ? themeMode === THEME.LIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText
                                                            : theme.palette.text.primary
                                                        }}
                                                    />
                                                }
                                            </>
                                            :
                                            <></>
                                        }
                                    </Stack>
                                </Button>
                            })
                        }
                    </Stack>

                    {/* Get a Quote and Theme Switch */}
                    <Stack
                        direction='row'
                        justifyContent='end'
                        alignItems='center'
                        spacing={{xs: -1, md: 2}}
                    >
                        {/* Get a Quote */}
                        <ButtonComponent 
                            label={t(`navigation.quote`)}
                            onClick={getAQuote}
                            sx={{
                                animation: 'bounceButton 1s infinite',
                                display: {xs: 'none', lg: 'block'}
                            }}
                        />

                        {/* Theme Switch */}
                        <FormControlLabel
                            control={<IOSSwitch sx={{ m: 1 }} />}
                            label=""
                            onChange={toggleTheme}
                            checked={themeMode === THEME.DARK}
                        />

                        <IconButton 
                            aria-label="menu"
                            size="large"
                            sx={{
                                display: {xs: 'block', lg: 'none'},
                                width: 60
                            }}
                            onClick={toggleDrawer(true)}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Stack>
                </Stack>
            </Card>

            {/* Pc Sub Menu */}
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={openSubMenu}
                onClose={() => handleSubMenuClose(null)}
                slotProps={{
                    list: {
                        'aria-labelledby': 'basic-button',
                        sx: {
                            backgroundColor: theme.palette.background.default,
                            p: 2
                        }
                    },
                }}
                elevation={2}
            >
                {
                    expandedMenu?.sub_menu.map((item, index) => {
                        return <MenuItem 
                            key={index}
                            onClick={() => handleSubMenuClose(item.to)}
                            sx={{
                                backgroundColor: theme.palette.background.default,
                                py: 1.5,
                                '&:hover': {
                                    backgroundColor: 'transparent'
                                }
                            }}
                            disableRipple
                            disableTouchRipple
                            className="onPointer"
                        >
                            <Typography
                                sx={{
                                    fontWeight: location?.pathname.includes(item.to) ? 600 : 400,
                                    fontSize: {xs: 12, md: 16},
                                    textAlign: 'left',
                                    color: location?.pathname.includes(item.to) 
                                    ? themeMode === THEME.LIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText
                                    : theme.palette.text.primary,
                                    '&:hover': {
                                        color: themeMode === THEME.LIGHT ? theme.palette.primary.main : theme.palette.primary.contrastText
                                    }
                                }}
                            >
                                {t(`navigation.suite_submenu.${item.label}`)}
                            </Typography>
                        </MenuItem>
                    })
                }
            </Menu>

            {/* Mobile Menu */}
            <Drawer 
                open={openMobileMenu} 
                onClose={toggleDrawer(false)}
                anchor="right"
                slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: theme.palette.primary.main
                        }
                    }
                }}
                ModalProps={{
                    keepMounted: true
                }}
            >
                <Box 
                    sx={{ 
                        width: {xs: 200, md: 300}
                    }} 
                    role="presentation" 
                    pt={3}
                >
                    <List>
                        <Stack
                            direction='column'
                            justifyContent='start'
                            alignItems='center'
                            spacing={2}
                        >
                            {
                                NAV_OPTIONS.map((item, index) => (
                                    <Accordion
                                        key={index}
                                        elevation={0}
                                        sx={{
                                            width: '80%',
                                            backgroundColor: 'transparent',
                                            '&:before': {
                                                display: 'none'
                                            }
                                        }}
                                        disableGutters
                                        onChange={(event) => {
                                            if(item.sub_menu.length > 0) return;

                                            setOpenMobileMenu(false);
                                            navigate(item.to);
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={item.sub_menu.length > 0 ? <ExpandMoreIcon style={{color: theme.palette.primary.contrastText}}/> : <></>}
                                            aria-controls="panel2-content"
                                            id="panel2-header"
                                        >
                                            <Typography 
                                                component="span"
                                                sx={{
                                                    fontWeight: location?.pathname.includes(item.to) ? 600 : 400,
                                                    fontSize: {xs: 14, md: 16},
                                                    textAlign: 'left',
                                                    color: theme.palette.primary.contrastText
                                                }}
                                                className="onPointer"
                                            >
                                                {t(`navigation.${item.label}`)}
                                            </Typography>
                                        </AccordionSummary>

                                        {
                                            item.sub_menu.length > 0
                                            ?
                                            <AccordionDetails
                                                sx={{
                                                    backgroundColor: 'transparent'
                                                }}
                                            >
                                                {
                                                    item.sub_menu.map((subMenuItem, subMenuIndex) => {
                                                        return <Button
                                                            key={subMenuIndex}
                                                            variant="text"
                                                            disableElevation
                                                            disableRipple
                                                            sx={{
                                                                fontWeight: location?.pathname.includes(subMenuItem.to) ? 600 : 400,
                                                                fontSize: {xs: 12, md: 16},
                                                                textAlign: 'left',
                                                                color: theme.palette.primary.contrastText,
                                                                textTransform: 'none',
                                                                py: 2
                                                            }}
                                                            className="onPointer"
                                                            onClick={() => {
                                                                setOpenMobileMenu(false);
                                                                navigate(item.to + "" + subMenuItem.to);
                                                            }}
                                                        >
                                                            {t(`navigation.suite_submenu.${subMenuItem.label}`)}
                                                        </Button>
                                                    })
                                                }
                                            </AccordionDetails>
                                            :
                                             <></>
                                        }
                                    </Accordion>
                                ))
                            }

                            <ButtonComponent 
                                label={t(`navigation.quote`)}
                                onClick={getAQuote}
                                sx={{
                                    width: '80%',
                                    animation: 'bounceButton 1s infinite',
                                    backgroundColor: theme.palette.primary.contrastText,
                                    color: theme.palette.primary.main
                                }}
                            />
                        </Stack>
                    </List>
                </Box>
            </Drawer>

            <GetQuote 
                open={openQuote}
                setOpen={setOpenQuote}
            />
        </>
    );
}

export default NavigationLayout;